import { productStore } from './productStore';
import { asAmount } from './budget';

/** A line of its own, so the hours stay what was reported and each chapter what its people cost. */
export const sprintDiscountKey = (productId: string, sprintId: string): string =>
  `productSprintDiscount:${productId}:${sprintId}`;

/** A sprint's adjustment, and why. The note is free text, and empty where nobody wrote one. */
export interface Discount {
  /** Money off the bill; negative is an extra charge, so stored discounts keep their meaning. */
  amount: number;
  note: string;
}

/** Discounts by sprint. A sprint with no entry was not discounted, which is not the same as zero. */
export type SprintDiscounts = Record<string, Discount>;

const asSignedAmount = (value: unknown): number | null => {
  const negative = typeof value === 'number' && value < 0;
  const amount = asAmount(negative ? -(value as number) : value);
  return amount === null || !negative ? amount : -amount;
};

// A value that does not parse reads as nothing discounted.
export const readDiscount = (productId: string, sprintId: string): Discount | null => {
  try {
    const raw = productStore.getItem(sprintDiscountKey(productId, sprintId));
    if (raw === null) return null;
    const parsed: unknown = JSON.parse(raw);
    // A bare number is what was stored before discounts carried a reason.
    const stored: Partial<Discount> | { amount: unknown; note?: unknown } =
      typeof parsed === 'object' && parsed !== null ? (parsed as Partial<Discount>) : { amount: parsed };
    const amount = asSignedAmount(stored.amount);
    if (amount === null) return null;
    return { amount, note: typeof stored.note === 'string' ? stored.note : '' };
  } catch {
    return null;
  }
};

/** A leading plus is what makes a typed figure an extra charge; anything else comes off. */
export const EXTRA_CHARGE = '+';

/** The figure as typed, as money off: `+50` is -50, and a bare `+` is nothing yet. */
export const typedAdjustment = (typed: string): number | null => {
  const text = typed.trim();
  const adds = text.startsWith(EXTRA_CHARGE);
  const figure = adds ? text.slice(1).trim() : text;
  if (figure === '') return null;
  const amount = asAmount(figure);
  if (amount === null || amount === 0) return amount;
  return adds ? -amount : amount;
};

/** The stored figure as the cell types it, so an extra charge opens with its plus. */
export const asTyped = (amount: number): string => (amount < 0 ? `${EXTRA_CHARGE}${-amount}` : String(amount));

export const writeDiscount = (productId: string, sprintId: string, discount: Discount | null): void => {
  const key = sprintDiscountKey(productId, sprintId);
  // Nothing off is the absence of a discount rather than a discount of nothing, so it is not stored.
  if (discount === null) productStore.removeItem(key);
  else productStore.setItem(key, JSON.stringify(discount));
};

/** The whole year at once, as the grid reads it. A sprint nobody discounted is not a key. */
export const readYearDiscounts = (productId: string, sprintIds: string[]): SprintDiscounts =>
  Object.fromEntries(
    sprintIds
      .map(sprintId => [sprintId, readDiscount(productId, sprintId)] as const)
      .filter((entry): entry is readonly [string, Discount] => entry[1] !== null),
  );
